import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, Check } from 'lucide-react';
import { SectionHeader } from './ui';
type Card = {
  title: string;
  items: string[];
  note: string;
};
const cards: Card[] = [{
  title: '7 noches de alojamiento en Posada Dois Irmãos',
  items: ['Alojamiento durante toda la estadía en una de las posadas más tradicionales de Fernando de Noronha, ubicada en una zona estratégica de fácil acceso a playas, restaurantes y principales atractivos de la isla.', 'Las habitaciones serán asignadas en base doble, triple o cuádruple según disponibilidad y orden de reserva. Cuanto antes reserves, mayores serán las posibilidades de acceder a las opciones de habitación más convenientes.'],
  note: 'Tu base ideal para disfrutar Fernando de Noronha durante toda la semana.'
}, {
  title: 'Almuerzos incluidos los días de buceo',
  items: ['5 almuerzos incluidos', 'Uno después de cada jornada de buceo'],
  note: 'Un diferencial exclusivo pensado para que aproveches cada día al máximo sin preocuparte por la logística.'
}, {
  title: 'Ilha Tour incluido',
  items: ['El paseo más famoso y recomendado de Fernando de Noronha. Una experiencia ideal para comenzar el viaje recorriendo los principales miradores, playas y puntos icónicos de la isla junto a guías locales especializados.', 'La mejor manera de descubrir Noronha desde el primer día y comprender por qué es considerada uno de los destinos más exclusivos de Brasil.'],
  note: 'La mejor manera de comenzar a conocer Fernando de Noronha.'
}, {
  title: 'Paseo en barco al atardecer',
  items: ['Una experiencia imperdible para contemplar Fernando de Noronha desde el mar.', 'Navegaremos por la costa de la isla disfrutando de paisajes únicos, aguas cristalinas y uno de los atardeceres más espectaculares de Brasil.', 'Una actividad perfecta para vivir Noronha desde una perspectiva completamente diferente y sumar un momento inolvidable a la experiencia.'],
  note: 'Un momento inolvidable para vivir Noronha desde el mar.'
}, {
  title: 'Caminatas y actividades en la isla',
  items: ['Caminatas guiadas', 'Visita a playas paradisíacas', 'Snorkeling en distintos puntos de la isla'],
  note: 'Una experiencia completa para descubrir todo lo que hace única a Noronha.'
}, {
  title: '10 inmersiones embarcadas',
  items: ['Realizaremos 10 inmersiones distribuidas en 5 salidas embarcadas visitando algunos de los sitios más icónicos de Fernando de Noronha, incluyendo Pedras Secas, Caverna da Sapata, Trinta Réis, Cabeço das Cordas y Macaxeira.', 'Cada jornada será planificada para aprovechar las mejores condiciones y ofrecer una experiencia de buceo excepcional.'],
  note: 'Aguas cristalinas, vida marina abundante y un ecosistema protegido reconocido mundialmente.'
}, {
  title: 'Encuentros con la fauna marina',
  items: ['Tortugas marinas', 'Delfines', 'Rayas', 'Tiburones', 'Peces tropicales'],
  note: 'Cada inmersión es una oportunidad para encontrarte cara a cara con la increíble biodiversidad de Noronha.'
}, {
  title: 'Staff acompañando todo el viaje',
  items: ['Staff de Buceo y Navegación presente durante toda la experiencia', 'Asistencia permanente', 'Coordinación personalizada'],
  note: 'Viajás acompañado por un equipo con experiencia real en destinos de buceo y viajes grupales.'
}];
function ExperienceCard({
  card,
  isOpen,
  onClick
}: {
  card: Card;
  isOpen: boolean;
  onClick: () => void;
}) {
  return <div className={`rounded-2xl border bg-noronha-graphite overflow-hidden transition-colors duration-300 ${isOpen ? 'border-noronha-emerald/50' : 'border-noronha-petroleum/30 hover:border-noronha-emerald/40'}`} data-id="element-131">
      <button onClick={onClick} aria-expanded={isOpen} className="w-full flex items-center gap-4 p-5 md:p-6 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-noronha-emerald/60 rounded-2xl group" data-id="element-132">
        <span className="shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-full bg-noronha-emerald/10 border border-noronha-emerald/40 flex items-center justify-center" aria-hidden="true" data-id="element-133">
          <Check className="w-5 h-5 md:w-6 md:h-6 text-noronha-emerald" strokeWidth={2.5} data-id="element-134" />
        </span>
        <span className="flex-1 font-heading text-lg md:text-xl text-noronha-text-heading tracking-tight group-hover:text-noronha-emerald transition-colors" data-id="element-135">
          {card.title}
        </span>
        <span className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center border transition-colors duration-300 ${isOpen ? 'bg-noronha-emerald border-noronha-emerald text-noronha-volcanic' : 'border-noronha-petroleum text-noronha-gold'}`} data-id="element-136">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-137" /> : <Plus className="w-4 h-4" data-id="element-138" />}
        </span>
      </button>

      <AnimatePresence initial={false} data-id="element-139">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.35,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-140">
            <div className="px-5 md:px-6 pb-6 pl-5 md:pl-[5.5rem]" data-id="element-141">
              <ul className="flex flex-col gap-2.5 mb-5" data-id="element-142">
                {card.items.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-143">
                    <span className="mt-1 shrink-0 w-4 h-4 rounded-full bg-noronha-emerald/15 flex items-center justify-center" data-id="element-144">
                      <Check className="w-3 h-3 text-noronha-emerald" data-id="element-145" />
                    </span>
                    <span className="text-noronha-text-body text-sm md:text-base leading-relaxed" data-id="element-146">
                      {item}
                    </span>
                  </li>)}
              </ul>
              <p className="text-noronha-sand italic text-sm md:text-base leading-relaxed border-l-2 border-noronha-gold/40 pl-4" data-id="element-147">
                {card.note}
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function ExperienceIncludes() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <section id="incluye" className="relative bg-noronha-volcanic py-24 md:py-32" data-id="element-148">
      <div className="container mx-auto px-6" data-id="element-149">
        <SectionHeader eyebrow="Todo Incluido" title="¿Qué incluye tu experiencia?" description="Tocá cada tarjeta para descubrir todo lo que vivirás en Fernando de Noronha." className="mb-12 md:mb-16" data-id="element-150" />

        <div className="max-w-3xl mx-auto flex flex-col gap-4" data-id="element-151">
          {cards.map((card, idx) => <ExperienceCard key={idx} card={card} isOpen={openIndex === idx} onClick={() => setOpenIndex(openIndex === idx ? null : idx)} data-id="element-152" />)}
        </div>
      </div>
    </section>;
}